import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { User, LoginResponse } from "../../types/auth";
import {
  setAuthToken,
  setStoredUser,
  removeAuthData,
} from "../../utils/authentication";

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  error: string | null;
  emailVerified: boolean;
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  loading: false,
  error: null,
  emailVerified: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginRequest: (
      state,
      _action: PayloadAction<{ email: string; password: string }>,
    ) => {
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (state, action: PayloadAction<LoginResponse>) => {
      const userData: User = {
        company: action.payload.data.company,
        id: action.payload.data.id,
        email: action.payload.data.email,
        firstName: action.payload.data.firstName,
        lastName: action.payload.data.lastName,
        roles: action.payload.data.roles,
        sharePointCredsValid: action.payload.data.sharePointCredsValid,
      };

      state.isAuthenticated = true;
      state.user = userData;
      state.loading = false;
      state.error = null;

      // Store user data and token
      setStoredUser(userData);
      setAuthToken(action.payload.data.accessToken);
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
      state.isAuthenticated = false;
      state.user = null;
      removeAuthData();
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.error = null;
      removeAuthData();
    },
    // Signup actions
    signupSharePointRequest: (
      state,
      _action: PayloadAction<{ token: string; sharePointKey: string }>,
    ) => {
      state.loading = true;
      state.error = null;
    },
    signupPasswordRequest: (
      state,
      _action: PayloadAction<{ token: string; password: string }>,
    ) => {
      state.loading = true;
      state.error = null;
    },
    signupPasswordSuccess: (state) => {
      state.loading = false;
      state.error = null;
    },
    signupSharePointSuccess: (state, action: PayloadAction<LoginResponse>) => {
      const userData: User = {
        company: action.payload.data.company,
        id: action.payload.data.id,
        email: action.payload.data.email,
        firstName: action.payload.data.firstName,
        lastName: action.payload.data.lastName,
        roles: action.payload.data.roles,
        sharePointCredsValid: action.payload.data.sharePointCredsValid,
      };

      state.isAuthenticated = true;
      state.user = userData;
      state.loading = false;
      state.error = null;

      setStoredUser(userData);
      setAuthToken(action.payload.data.accessToken);
    },
    // Email verification actions
    verifyEmailRequest: (state, _action: PayloadAction<string>) => {
      state.loading = true;
      state.error = null;
    },
    verifyEmailSuccess: (state) => {
      state.loading = false;
      state.error = null;
      state.emailVerified = true;
    },
    verifyEmailFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
      state.emailVerified = false;
    },
    // SharePoint config check actions
    checkSharePointConfig: (state) => {
      state.loading = true;
      state.error = null;
    },
    checkSharePointConfigSuccess: (
      state,
      action: PayloadAction<{
        success: boolean;
      }>,
    ) => {
      if (state.user) {
        state.user.sharePointCredsValid = action.payload.success;
      }
      state.loading = false;
    },
    checkSharePointConfigFailure: (state, action: PayloadAction<string>) => {
      if (state.user) {
        state.user.sharePointCredsValid = false;
      }
      state.error = action.payload;
      state.loading = false;
    },
  },
});

export const {
  loginRequest,
  loginSuccess,
  loginFailure,
  logout,
  signupSharePointRequest,
  signupPasswordRequest,
  signupPasswordSuccess,
  signupSharePointSuccess,
  verifyEmailRequest,
  verifyEmailSuccess,
  verifyEmailFailure,
  checkSharePointConfig,
  checkSharePointConfigSuccess,
  checkSharePointConfigFailure,
} = authSlice.actions;

export default authSlice.reducer;
